import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, firstValueFrom, retry, timer } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { ProfessionalsService } from './professionals.service';
import { AppRole } from '../models/permissions.model';

/**
 * Structured post-login phase emitted to UI components.
 * Consumed by PostLoginStatusComponent.
 */
export type PostLoginPhase =
  | { phase: 'verifica' }
  | { phase: 'attesa'; countdown: number }
  | { phase: 'finalizzazione' }
  | { phase: 'negato' };

/**
 * User roles supported by Azure AD
 */
export type UserRole = 
  | 'admin'
  | 'backoffice_admin'
  | 'backoffice_professional'
  | 'backoffice_supplier'
  | 'professional'
  | 'collaborator';

/**
 * User profile extracted from JWT token
 */
export interface UserProfile {
  id: string;
  email: string;
  name: string;
  roles: UserRole[];
  professionalId?: string; // ✅ Professional ID from JWT custom claim (if configured)
  exp?: number; // Token expiration timestamp
}

/**
 * Result of resolving the professional profile after login.
 * - 'found'     → professional_id resolved (user is registered)
 * - 'not_found' → backend confirmed the user has no profile yet (needs pre-registration)
 * - 'error'     → transient/unknown failure: status could NOT be determined.
 *                 Callers MUST NOT treat this as "unregistered".
 */
type ProfileCheckResult =
  | { status: 'found'; professionalId: string }
  | { status: 'not_found' }
  | { status: 'error' };

/**
 * JWT Token payload structure
 */
interface JwtPayload {
  sub: string;
  email?: string;
  name?: string;
  preferred_username?: string;
  given_name?: string;
  family_name?: string;
  roles?: string[];
  oid?: string; // Object ID from Azure AD
  exp?: number;
  iat?: number;
  [key: string]: any;
}

/**
 * Authentication Service for Azure AD integration
 *
 * Security model (CVE fix — Session Hijacking):
 * - access_token stored IN MEMORY only (_accessToken field)
 * - refresh_token stored in HttpOnly Secure cookie managed by pal-dmn-core BFF
 * - Neither token is ever written to localStorage or sessionStorage
 * - User *profile* (non-sensitive) is kept in sessionStorage for UX continuity
 *
 * Handles:
 * - Login/Logout via Azure AD OAuth2 + PKCE
 * - Token management (in-memory access_token + HttpOnly cookie refresh_token)
 * - JWT token parsing and validation
 * - User profile and role management
 * 
 * @example
 * ```typescript
 * // In component
 * constructor(private authService: AuthService) {}
 * 
 * login() {
 *   this.authService.login();
 * }
 * 
 * logout() {
 *   this.authService.logout();
 * }
 * 
 * checkRole() {
 *   if (this.authService.hasRole('backoffice_admin')) {
 *     // Admin actions
 *   }
 * }
 * ```
 */
@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private router = inject(Router);
  private http = inject(HttpClient);
  private professionalsService = inject(ProfessionalsService);

  /** Emits the current post-login phase for UI loading states */
  readonly postLoginStep$ = new BehaviorSubject<PostLoginPhase | null>(null);
  
  /** Stores access denial reason (403, no roles, etc.) - exposed to UI */
  readonly accessDenialReason$ = new BehaviorSubject<string | null>(null);
  
  // Observable for current user profile
  private currentUserSubject = new BehaviorSubject<UserProfile | null>(null);
  public currentUser$ = this.currentUserSubject.asObservable();

  /**
   * SECURITY: access_token lives ONLY in memory — never in localStorage/sessionStorage.
   * XSS cannot read a plain JS variable that is not persisted anywhere.
   * The token is lost on page refresh, but is restored transparently via
   * the HttpOnly refresh_token cookie (see _initializeSession).
   */
  private _accessToken: string | null = null;
  private _interactiveReauthInProgress = false;

  /**
   * Effective roles fetched from pal-dmn-core GET /api/v1/me/roles
   * (header/CIAM enrichment). When present, they are merged with the roles
   * decoded from the JWT so getCurrentUser()/parseToken() reflect them.
   * Null until loaded; cleared on logout.
   */
  private _serverRoles: UserRole[] | null = null;

  /**
   * Deduplicates concurrent silent-refresh calls triggered by the HTTP interceptor.
   * If multiple requests get a 401 simultaneously, they all share this single Promise
   * and receive the same result without issuing multiple BFF calls.
   */
  private _refreshPromise: Promise<string | null> | null = null;

  /**
   * Resolves once the startup silent-refresh attempt has completed.
   * AuthGuard awaits this before deciding whether the user is authenticated,
   * preventing a redirect to /login while the refresh is still in flight.
   */
  public readonly initialized: Promise<void>;

  constructor() {
    this.initialized = this._initializeSession();
  }

  /**
   * On app startup, try to restore the session from the HttpOnly refresh_token
   * cookie.  If the server returns a new access_token we are good to go.
   * If it returns 401 (no cookie / expired) the user will be asked to log in.
   *
   * In mock-auth mode (useMockAuth=true), skips the BFF call entirely and
   * restores the mock token from sessionStorage so that F5 doesn't logout.
   */
  private async _initializeSession(): Promise<void> {
    // Populate the BehaviorSubject from sessionStorage profile if available
    // (no-op if missing — the token itself is never persisted).
    this.loadUserFromStorage();

    // ── Mock-auth shortcut (local dev only) ──────────────────────────────────
    // Active only when useMockAuth=true. Restores the mock token stored in
    // sessionStorage so that F5 doesn't redirect back to /auth-test.
    if (environment.useMockAuth) {
      const mockToken = sessionStorage.getItem('mock_access_token');
      if (mockToken) {
        this._accessToken = mockToken;
        const profile = this.parseToken(mockToken);
        if (profile) {
          this.currentUserSubject.next(profile);
        }
        console.log('🎭 [AuthService] Mock session restored from sessionStorage');
      } else {
        console.log('🎭 [AuthService] No mock session found — redirect to /auth-test');
      }
      return; // skip real BFF call in mock mode
    }
    // ─────────────────────────────────────────────────────────────────────────

    try {
      const newToken = await this.refreshAzureToken();
      if (newToken) {
        this._accessToken = newToken.replace(/\s+/g, '').trim();
        // Fetch server-side effective roles (header/CIAM enrichment) so that,
        // after a page refresh (F5), onboarding users whose roles are not
        // carried in the JWT keep their enriched roles. Fails silently.
        await this.loadServerRoles();
        const profile = this.parseToken(this._accessToken);
        if (profile) {
          this.currentUserSubject.next(profile);
          sessionStorage.setItem('user_profile', JSON.stringify(profile));
          sessionStorage.setItem('user', JSON.stringify(profile));
        }
        console.log('✅ [AuthService] Session restored via silent refresh');
      } else {
        console.log('ℹ️ [AuthService] No active session cookie — user must login');
      }
    } catch (e) {
      // Guarantee that `initialized` always resolves (never rejects) so that
      // AuthGuard and the login component are never left waiting indefinitely.
      console.warn('⚠️ [AuthService] _initializeSession error (non-blocking):', e);
    }
  }

  /**
   * Redirects to Azure AD login page
   * Uses OAuth2 Authorization Code Flow with PKCE
   */
  async login(returnUrl?: string): Promise<void> {
    console.log('====== AUTH SERVICE LOGIN ======');
    console.log('🔐 login() called with returnUrl:', returnUrl);
    
    // Store returnUrl in sessionStorage (more reliable than OAuth state with CIAM)
    if (returnUrl) {
      sessionStorage.setItem('auth_return_url', returnUrl);
      console.log('✅ ReturnUrl stored in sessionStorage');
      console.log('  - Key: auth_return_url');
      console.log('  - Value:', returnUrl);
      
      // Verify it was stored
      const stored = sessionStorage.getItem('auth_return_url');
      if (stored === returnUrl) {
        console.log('✅ Verification: sessionStorage write successful');
      } else {
        console.error('❌ Verification FAILED: stored value does not match');
        console.error('  - Expected:', returnUrl);
        console.error('  - Found:', stored);
      }
    } else {
      console.log('ℹ️ No returnUrl provided, will not store in sessionStorage');
    }

    // Generate PKCE code verifier and challenge
    const codeVerifier = this.generateCodeVerifier();
    const codeChallenge = await this.generateCodeChallenge(codeVerifier);
    
    // Store code verifier for token exchange
    sessionStorage.setItem('pkce_code_verifier', codeVerifier);

    // Build Azure AD authorization URL with PKCE
    const authUrl = this.buildAuthorizationUrl(codeChallenge);
    
    console.log('🔐 PKCE Login initiated:');
    console.log('  - Code Verifier (first 10 chars):', codeVerifier.substring(0, 10) + '...');
    console.log('  - Code Challenge (first 10 chars):', codeChallenge.substring(0, 10) + '...');
    console.log('  - Redirect URI:', environment.azureAd?.redirectUri);
    if (returnUrl) {
      console.log('🔗 ReturnUrl preserved in sessionStorage (will survive Azure AD redirect)');
    }
    console.log('  - Auth URL (first 100 chars):', authUrl.substring(0, 100) + '...');
    console.log('================================');
    console.log('🚀 Redirecting to Azure AD...');
    
    window.location.href = authUrl;
  }

  /**
   * Handles OAuth callback after Azure AD login
   * Extracts authorization code and exchanges it for access token
   */
  async handleCallback(): Promise<boolean> {
    console.log('====== OAUTH CALLBACK HANDLER ======');
    console.log('🔙 handleCallback() invoked');
    console.log('  - Current URL:', window.location.href);
    
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state');
    const error = params.get('error');
    const errorDescription = params.get('error_description');

    console.log('🔙 OAuth Callback parameters:');
    console.log('  - Has code:', !!code, code ? `(${code.substring(0, 20)}...)` : '');
    console.log('  - Has state:', !!state, state ? `(length: ${state.length})` : '');
    console.log('  - Has error:', !!error, error || '');
    if (state) {
      console.log('  - State preview:', state.substring(0, 50) + '...');
    }
    
    // Check sessionStorage for returnUrl
    const returnUrlInStorage = sessionStorage.getItem('auth_return_url');
    console.log('📦 SessionStorage check:');
    console.log('  - auth_return_url exists:', !!returnUrlInStorage);
    if (returnUrlInStorage) {
      console.log('  - auth_return_url value:', returnUrlInStorage);
    } else {
      console.log('  - No auth_return_url found in sessionStorage');
    }

    if (error) {
      console.error('❌ Azure AD authentication error:', error, errorDescription);
      return false;
    }

    if (!code) {
      console.warn('⚠️ No authorization code found in callback');
      return false;
    }

    try {
      console.log('🔄 Exchanging authorization code for token...');
      // Exchange code for token
      const token = await this.exchangeCodeForToken(code);
      
      if (token) {
        console.log('✅ Token exchange successful');
        const ok = await this.completeLoginWithToken(token);
        if (!ok) {
          // Access denied (no roles or 403) - logout and let caller show error
          this.logout();
        }
        return ok;
      }
    } catch (error) {
      console.error('❌ Error exchanging code for token:', error);
    }

    return false;
  }

  /**
   * Completes the login flow after having a valid token.
   * Shared between the real OAuth callback and auth-test simulation.
   * 
   * Flow:
   * 1. Store token + extract user profile
   * 2. If user already has roles → redirect immediately (no backend call)
   * 3. If no roles → POST /api/auth/post-login → wait 20s → refresh token
   * 4. Re-check roles; logout if still none
   * 
   * @param token - Valid Azure AD access token
   * @returns Promise<boolean> - true if login completed successfully
   */
  async completeLoginWithToken(token: string): Promise<boolean> {
    this.setToken(token);

    // Fetch effective roles from the server (header/CIAM enrichment) so that
    // onboarding users whose roles are not carried in the token are recognized.
    // Fails silently, falling back to JWT-decoded roles.
    await this.loadServerRoles();

    // Check if user already has valid roles in the token
    const user = this.getCurrentUser();
    const hasRoles = (user?.roles?.length ?? 0) > 0;

    if (hasRoles) {
      // User already has roles - proceed directly, no need to call post-login
      console.log('✅ User already has roles, proceeding to redirect');
      await this.redirectAfterAuthentication();
      return true;
    }

    // ── No roles: trigger post-login CIAM group assignment ────────────
    console.log('⚠️ User has no roles - triggering post-login assignment flow...');
    const postLoginOk = await this.runPostLoginFlow(token);
    if (!postLoginOk) {
      return false;
    }
    // ─────────────────────────────────────────────────────────────────

    // Re-check roles after post-login + token refresh
    const userAfterFlow = this.getCurrentUser();
    const hasRolesNow = (userAfterFlow?.roles?.length ?? 0) > 0;

    if (!hasRolesNow) {
      console.error('❌ No valid roles after post-login flow - access denied');
      this.accessDenialReason$.next('Nessun ruolo assegnato all\'utente. Contattare l\'amministratore.');
      this.postLoginStep$.next(null);
      return false; // Caller is responsible for logout/redirect
    }

    await this.redirectAfterAuthentication();
    return true;
  }

  /**
   * Runs the post-login CIAM flow:
   * 1. POST /api/auth/post-login  → triggers group assignment if needed
   * 2. Wait 3 s for Azure AD propagation
   * 3. Refresh token (forceRefresh via new silent PKCE-less token exchange)
   * Returns false if access should be denied (403).
   * 
   * Note: Uses HttpClient to benefit from Angular proxy configuration in local dev.
   */
  async runPostLoginFlow(initialToken: string): Promise<boolean> {
    try {
      this.postLoginStep$.next({ phase: 'verifica' });
      console.log('🔄 [PostLogin] Calling POST /api/auth/post-login');

      const coreUrl = environment.coreApiUrl;
      const url = `${coreUrl}/api/auth/post-login`;
      
      console.log('🔗 [PostLogin] Request URL:', url);
      console.log('🔗 [PostLogin] Environment coreApiUrl:', coreUrl);

      // Use HttpClient instead of fetch to benefit from Angular proxy (proxy.conf.json)
      const response = await firstValueFrom(
        this.http.post<any>(url, {}, {
          headers: {
            'Authorization': `Bearer ${initialToken}`,
            'Content-Type': 'application/json',
          },
          observe: 'response', // Get full response with status
        })
      );

      if (!response) {
        console.warn('⚠️ [PostLogin] No response received');
        this.postLoginStep$.next(null);
        return true; // Non-blocking
      }

      console.log('✅ [PostLogin] Response status:', response.status);

      // Wait for Azure AD group propagation with countdown (called only when user has no roles)
      // Doc integrazione CIAM: attendere 3s per propagazione gruppo in Azure AD
      const waitSeconds = 3;
      console.log(`⏳ [PostLogin] Waiting ${waitSeconds}s for Azure AD propagation...`);
      for (let remaining = waitSeconds; remaining > 0; remaining--) {
        this.postLoginStep$.next({ phase: 'attesa', countdown: remaining });
        await new Promise(resolve => setTimeout(resolve, 1000));
      }

      // Refresh token
      this.postLoginStep$.next({ phase: 'finalizzazione' });
      const newToken = await this.refreshAzureToken();
      if (newToken) {
        console.log('✅ [PostLogin] Token refreshed successfully');
        this.setToken(newToken);
      } else {
        // Refresh failed - continue with old token (non-blocking)
        console.warn('⚠️ [PostLogin] Token refresh failed - continuing with initial token');
      }

      this.postLoginStep$.next(null);
      return true;
    } catch (error: any) {
      // Handle HTTP errors (403, 401, etc.)
      if (error?.status === 403) {
        console.warn('⛔ [PostLogin] Access denied (403)');
        this.postLoginStep$.next({ phase: 'negato' });
        this.accessDenialReason$.next('L\'utente non soddisfa i requisiti d\'accesso (403)');
        return false;
      }

      if (error?.status === 401) {
        console.warn('⚠️ [PostLogin] Unauthorized (401)');
        this.postLoginStep$.next(null);
        return false;
      }

      // Network error or unexpected failure - non-blocking
      console.warn('⚠️ [PostLogin] Error during post-login flow (non-blocking):', error);
      this.postLoginStep$.next(null);
      return true; // Continue login despite post-login error
    }
  }

  /**
   * Refreshes the access_token by calling the BFF silent-refresh endpoint.
   * The browser automatically includes the HttpOnly refresh_token cookie,
   * so there is no token in sessionStorage/localStorage to steal.
   */
  private async refreshAzureToken(): Promise<string | null> {
    const url = `${environment.coreApiUrl}/api/auth/silent-refresh`;
    try {
      const data = await firstValueFrom(
        this.http.post<{ access_token: string; expires_in: number }>(url, {},
          { withCredentials: true } // sends the HttpOnly cookie automatically
        )
      );
      console.log('✅ [TokenRefresh] Silent refresh via BFF succeeded');
      return data?.access_token ?? null;
    } catch (e: any) {
      if (e?.status === 401) {
        console.log('ℹ️ [TokenRefresh] No active session cookie (expected on first login)');
      } else {
        console.warn('⚠️ [TokenRefresh] Silent refresh failed - status:', e?.status, '| type:', e?.constructor?.name, '| message:', e?.message, '| full error:', e);
      }
      return null;
    }
  }

  /**
   * Public entry point for the HTTP interceptor.
   * Attempts a BFF silent-refresh and stores the new access_token in memory.
   * Deduplicates concurrent calls: if a refresh is already in flight, all
   * callers await the same Promise and receive the same result.
   *
   * @returns The new access_token string, or null if the refresh_token cookie
   *          is missing/expired (user must re-authenticate).
   */
  async trySilentRefresh(): Promise<string | null> {
    if (this._refreshPromise) {
      console.log('🔄 [SilentRefresh] Refresh already in progress — queuing');
      return this._refreshPromise;
    }
    this._refreshPromise = this.refreshAzureToken()
      .then(newToken => {
        if (newToken) {
          this.setToken(newToken);
          console.log('✅ [SilentRefresh] Token refreshed and stored in memory');
        }
        return newToken;
      })
      .finally(() => {
        this._refreshPromise = null;
      });
    return this._refreshPromise;
  }

  /**
   * Starts a full interactive re-authentication flow and preserves the current page
   * so the user can be redirected back after login.
   * Used when the session is effectively expired and silent refresh cannot recover it.
   */
  async startInteractiveReauthentication(
    returnUrl?: string,
    reason: string = 'token_expired',
    message: string = 'Sessione scaduta. Effettua nuovamente il login.'
  ): Promise<void> {
    if (this._interactiveReauthInProgress) {
      console.log('🔄 [AuthService] Interactive re-authentication already in progress');
      return;
    }

    this._interactiveReauthInProgress = true;

    const candidateReturnUrl = returnUrl || this.router.url || '/';

    // Never store an auth/error page as the post-login destination — otherwise
    // the user could be looped back to the session-expired/login page after
    // re-authenticating. Fall back to any previously stored working URL.
    const isAuthPage = /\/(login|session-expired|unauthorized|auth\/callback|auth-test)/.test(
      candidateReturnUrl,
    );
    const targetReturnUrl = isAuthPage
      ? sessionStorage.getItem('auth_return_url') || '/'
      : candidateReturnUrl;

    sessionStorage.setItem('auth_error_reason', reason);
    sessionStorage.setItem('auth_error_message', message);
    sessionStorage.setItem('auth_return_url', targetReturnUrl);

    console.warn('🔐 [AuthService] Starting interactive re-authentication');
    console.warn('  - reason:', reason);
    console.warn('  - returnUrl:', targetReturnUrl);

    this._accessToken = null;
    this.currentUserSubject.next(null);

    // UX: instead of bouncing the user straight to Azure AD (or showing the
    // generic /unauthorized page), present a dedicated "session expired" page
    // with a single, clearly-labelled re-login action. The returnUrl is kept in
    // sessionStorage so redirectAfterAuthentication() brings the user back to
    // exactly where they left off after re-authenticating.
    const sessionExpiredRoute = environment.useMockAuth ? '/auth-test' : '/session-expired';

    // Reset the in-progress flag: navigating to the session-expired page does not
    // start the OAuth redirect yet — that happens when the user clicks re-login.
    this._interactiveReauthInProgress = false;

    this.router.navigate([sessionExpiredRoute]).catch(() => {
      window.location.href = sessionExpiredRoute;
    });
  }

  /**
   * Check if professional profile exists and populate sessionStorage
   * Called after login for professional/collaborator roles
   * 
   * Returns: professional_id if found, null otherwise
   * 
   * SECURITY OPTIMIZATION:
   * - First checks if professional_id is in JWT token (most secure - signed cryptographically)
   * - Falls back to API call /auth/profile if not in token
   */
  private async checkProfessionalProfile(): Promise<ProfileCheckResult> {
    console.log('🔍 Checking professional profile...');
    
    // Check if professional_id is already in JWT token (best case)
    const user = this.getCurrentUser();
    if (user?.professionalId) {
      console.log('🎫 Professional ID found in JWT token (secure):', user.professionalId);
      console.log('✅ Skipping API call - using professional_id from signed JWT');
      
      // Save to sessionStorage for compatibility
      sessionStorage.setItem('professional_id', user.professionalId);
      sessionStorage.setItem('professional_profile', JSON.stringify({
        professionalId: user.professionalId,
        // Other fields would come from API if needed
      }));
      
      return { status: 'found', professionalId: user.professionalId };
    }
    
    // Fallback: professional_id not in JWT - call API (with retry on transient errors).
    // A registered user MUST NOT be misrouted to pre-registration just because this
    // single call failed transiently (network blip, 5xx, timeout, token race).
    console.log('📞 Professional ID not in JWT - calling API: /api/v1/auth/profile (PAL BFF Professional)');
    
    try {
      const response = await firstValueFrom(
        this.professionalsService.getAuthProfile().pipe(
          retry({
            count: 2,
            delay: (error: any, retryCount: number) => {
              const status = error?.status;
              const isTransient =
                status === 0 || status === 429 || (typeof status === 'number' && status >= 500);
              if (!isTransient) {
                // Non-transient (404 / 401 / 403) - fail fast, do not retry
                throw error;
              }
              console.warn(`⏳ [ProfileCheck] Transient error (status ${status}), retry ${retryCount}/2...`);
              return timer(retryCount * 800);
            },
          }),
        ),
      );
      
      if (response && response.professionalId) {
        // Professional profile found - save data to sessionStorage
        console.log('✅ Professional profile found:', response);
        
        sessionStorage.setItem('professional_id', response.professionalId);
        sessionStorage.setItem('professional_profile', JSON.stringify(response));
        
        return { status: 'found', professionalId: response.professionalId };
      }
      
      // Response received but without a professional_id → genuinely not registered yet
      console.log('⚠️ No professional profile found (empty response) → pre-registration');
      return { status: 'not_found' };
    } catch (error: any) {
      // 404 / NOT_FOUND = backend confirms the user has no profile yet → pre-registration is correct
      if (error?.error?.errorCode === 'NOT_FOUND' || error?.status === 404) {
        console.log('⚠️ Professional profile not found (NOT_FOUND) → pre-registration');
        return { status: 'not_found' };
      }
      
      // Any OTHER error (network, 5xx after retries, timeout, 401/403) is transient/unknown.
      // We cannot confirm the user is unregistered, so we DO NOT send them to pre-registration.
      console.error('❌ Error checking professional profile (transient/unknown - NOT routing to pre-registration):', error);
      return { status: 'error' };
    }
  }

  /**
   * Redirect user after authentication based on their role
   * This is a reusable method that can be called from:
   * - OAuth callback (after Azure AD login)
   * - Manual token authentication (auth-test)
   * - Any other authentication flow
   * 
   * @returns Promise<void>
   */
  async redirectAfterAuthentication(): Promise<void> {
    console.log('====== REDIRECT AFTER AUTHENTICATION ======');
    
    // Check user roles
    const user = this.getCurrentUser();
    console.log('👤 User data:');
    console.log('  - Name:', user?.name);
    console.log('  - Email:', user?.email);
    console.log('  - Roles:', user?.roles);
    
    // Check if there's a returnUrl (deep link from email) in sessionStorage
    const returnUrl = sessionStorage.getItem('auth_return_url');
    
    const isBackofficeUser = user?.roles.some(role => 
      role === AppRole.BACKOFFICE_SUPPLIER || 
      role === AppRole.BACKOFFICE_PROFESSIONAL ||
      role === AppRole.BACKOFFICE_ADMIN ||
      role === AppRole.SUPER_ADMIN
    );
    
    const isProfessionalOrCollaborator = user?.roles.some(role => 
      role === AppRole.PROFESSIONAL || role === AppRole.COLLABORATOR
    );

    console.log('  - Is backoffice user:', isBackofficeUser);
    console.log('  - Is professional/collaborator:', isProfessionalOrCollaborator);

    const profileResult = !isBackofficeUser && isProfessionalOrCollaborator
      ? await this.checkProfessionalProfile()
      : null;

    if (profileResult?.status === 'not_found') {
      this.router.navigate(['/axa/pre-registration']);
      return;
    }

    if (profileResult?.status === 'error') {
      const sessionExpiredRoute = environment.useMockAuth ? '/auth-test' : '/session-expired';
      this.router.navigate([sessionExpiredRoute]).catch(() => {
        window.location.href = sessionExpiredRoute;
      });
      return;
    }

    if (returnUrl) {
      sessionStorage.removeItem('auth_return_url');
      this.router.navigateByUrl(returnUrl);
      return;
    }

    // IMPORTANT: Check backoffice FIRST (has priority over professional/collaborator)
    if (isBackofficeUser) {
      const isSupplierOnly = user?.roles.some(role => role === AppRole.BACKOFFICE_SUPPLIER) &&
        !user?.roles.some(role =>
          role === AppRole.BACKOFFICE_PROFESSIONAL ||
          role === AppRole.BACKOFFICE_ADMIN ||
          role === AppRole.SUPER_ADMIN
        );
      if (isSupplierOnly) {
        console.log('🏢 Supplier backoffice user detected, redirecting to suppliers dashboard');
        console.log('===========================================');
        this.router.navigate(['/axa/suppliers-dashboard']);
      } else {
        console.log('💼 Backoffice user detected, redirecting to professionals dashboard');
        console.log('===========================================');
        this.router.navigate(['/axa/professionals-dashboard']);
      }
    } else if (profileResult?.status === 'found') {
      this.router.navigate(['/axa/professionals-registration-progress']);
    } else {
      console.log('ℹ️ Generic user, using default route');
      const defaultRoute = this.getDefaultRouteForUser();
      console.log('  - Default route:', defaultRoute);
      console.log('===========================================');
      this.router.navigate([defaultRoute]);
    }
  }

  /**
   * Logs out user and clears all auth data
   * Optionally redirects to Azure AD logout endpoint
   */
  logout(redirectToAzure: boolean = false): void {
    console.log('👋 [AuthService] Starting logout...');
    
    // 1. Clear in-memory token immediately
    this._accessToken = null;
    this._serverRoles = null;
    this.currentUserSubject.next(null);
    
    // 2. Clear session-scoped user profile (no token stored here, but clean up anyway)
    sessionStorage.removeItem('user');
    sessionStorage.removeItem('user_profile');
    sessionStorage.removeItem('auth_return_url');
    sessionStorage.removeItem('professional_id');
    sessionStorage.removeItem('professional_profile');
    sessionStorage.removeItem('auth_state');
    sessionStorage.removeItem('auth_code_verifier');
    sessionStorage.removeItem('refresh_token'); // legacy key, may be present on older browsers
    sessionStorage.removeItem('mock_access_token'); // mock mode: clear persisted mock token
    
    // 3. Remove legacy localStorage entries that may exist from older app versions
    localStorage.removeItem('access_token');
    localStorage.removeItem('user');
    localStorage.removeItem('user_profile');
    localStorage.removeItem('auth_return_url');
    
    // 4. Tell the backend to invalidate the HttpOnly refresh_token cookie.
    //    Fire-and-forget: even if this fails the token will expire server-side.
    const coreUrl = environment.coreApiUrl;
    this.http.post(`${coreUrl}/api/auth/logout`, {}, { withCredentials: true }).subscribe({
      error: (e) => console.warn('⚠️ [AuthService] Backend logout call failed (non-blocking):', e),
    });

    console.log('✅ [AuthService] Local auth state cleared');

    if (redirectToAzure && environment.azureAd) {
      console.log('🔄 [AuthService] Redirecting to Azure AD logout...');
      const postLogoutRedirectUri = `${window.location.origin}/login`;
      const logoutUrl = `https://login.microsoftonline.com/${environment.azureAd.tenantId}/oauth2/v2.0/logout?post_logout_redirect_uri=${encodeURIComponent(postLogoutRedirectUri)}`;
      window.location.href = logoutUrl;
    } else {
      const loginRoute = environment.useMockAuth ? '/auth-test' : '/login';
      console.log(`🔄 [AuthService] Redirecting to ${loginRoute}...`);
      this.router.navigate([loginRoute]).then((success) => {
        if (!success) {
          console.error(`❌ [AuthService] Navigation to ${loginRoute} failed, using window.location`);
          window.location.href = loginRoute;
        }
      }).catch(() => {
        window.location.href = loginRoute;
      });
    }
  }

  /**
   * Returns the current in-memory access_token.
   * SECURITY: token is NEVER read from localStorage/sessionStorage.
   */
  getAccessToken(): string | null {
    // Allow a static test token in environment (non-production testing only)
    const staticToken = (environment as any).staticTestToken;
    if (staticToken) {
      console.log('🔑 Using static test token from environment');
      return staticToken;
    }
    
    const token = this._accessToken;
    // NOTE: do NOT logout here on expiry.
    // Returning the expired token lets the HTTP interceptor send it to the BE,
    // receive a 401, and attempt a transparent silent refresh before falling
    // back to logout. Proactive logout here would bypass the refresh path.
    return token;
  }

  /**
   * Stores the access_token in memory and extracts the user profile.
   * SECURITY: token is stored in _accessToken only — never written to
   * localStorage or sessionStorage.
   */
  setToken(token: string): void {
    const cleanToken = token.replace(/\s+/g, '').trim();
    if (!cleanToken) {
      console.error('❌ Empty token provided');
      return;
    }
    console.log('🔑 Setting token in memory (length:', cleanToken.length, ')');
    this._accessToken = cleanToken;
    
    const profile = this.parseToken(cleanToken);
    if (profile) {
      // User profile (no token) stored in sessionStorage for UX continuity within the tab.
      // This is NOT sensitive — it does not contain the access_token.
      sessionStorage.setItem('user_profile', JSON.stringify(profile));
      sessionStorage.setItem('user', JSON.stringify(profile));
      this.currentUserSubject.next(profile);
      console.log('✅ Authenticated:', profile.email || profile.name);
    }
  }

  /**
   * Gets the current user profile
   * 
   * SECURITY: Always re-parses from JWT token to prevent localStorage tampering
   * DO NOT cache user profile - localStorage can be manipulated via DevTools
   */
  getCurrentUser(): UserProfile | null {
    // ✅ SECURE: Always re-parse from token instead of trusting cached value
    const token = this.getAccessToken();
    
    if (!token) {
      return null;
    }
    
    // Re-parse token to get fresh, validated user profile
    const profile = this.parseToken(token);
    
    // Update BehaviorSubject if profile changed
    if (profile && JSON.stringify(profile) !== JSON.stringify(this.currentUserSubject.value)) {
      this.currentUserSubject.next(profile);
      // Keep sessionStorage in sync (profile only, never the token)
      sessionStorage.setItem('user', JSON.stringify(profile));
      sessionStorage.setItem('user_profile', JSON.stringify(profile));
    }
    
    return profile;
  }

  /**
   * Checks if user is authenticated (has valid token)
   */
  isAuthenticated(): boolean {
    return !!this.getAccessToken();
  }

  /**
   * Clears only the in-memory token and user state WITHOUT navigation.
   * Intended for development/mock use only (MockAuthService.clearAuth).
   * In all other cases, call logout() which also clears the HttpOnly cookie.
   */
  clearTokenOnly(): void {
    this._accessToken = null;
    this.currentUserSubject.next(null);
    sessionStorage.removeItem('user');
    sessionStorage.removeItem('user_profile');
  }

  /**
   * Checks if user has a specific role
   */
  hasRole(role: UserRole): boolean {
    const user = this.getCurrentUser();
    // Case-insensitive comparison: JWT tokens may deliver roles in any casing
    // (e.g. BACKOFFICE_PROFESSIONAL vs backoffice_professional). Using strict
    // .includes() would silently fail for the entire session until re-login.
    return user?.roles?.some(r => r.toLowerCase() === role.toLowerCase()) ?? false;
  }

  /**
   * Checks if user has any of the specified roles
   */
  hasAnyRole(roles: UserRole[]): boolean {
    return roles.some(role => this.hasRole(role));
  }

  /**
   * Gets the default route for the current user based on their role
   * NOTE: For professional/collaborator, this is called as fallback only.
   * The main flow should go through checkProfessionalProfile() instead.
   */
  getDefaultRouteForUser(): string {
    const user = this.getCurrentUser();
    
    if (!user) {
      return '/login';
    }

    // Backoffice users go to dashboard
    if (user.roles.includes(AppRole.BACKOFFICE_SUPPLIER) || 
        user.roles.includes(AppRole.BACKOFFICE_PROFESSIONAL) ||
        user.roles.includes(AppRole.BACKOFFICE_ADMIN) ||
        user.roles.includes(AppRole.SUPER_ADMIN)) {
      const isSupplierOnly = user.roles.includes(AppRole.BACKOFFICE_SUPPLIER) &&
        !user.roles.includes(AppRole.BACKOFFICE_PROFESSIONAL) &&
        !user.roles.includes(AppRole.BACKOFFICE_ADMIN) &&
        !user.roles.includes(AppRole.SUPER_ADMIN);
      return isSupplierOnly
        ? '/axa/suppliers-dashboard'
        : '/axa/professionals-dashboard';
    }

    // Professional and Collaborator - fallback route
    // (should normally be handled by checkProfessionalProfile in callback)
    if (user.roles.includes(AppRole.PROFESSIONAL) || user.roles.includes(AppRole.COLLABORATOR)) {
      console.log('⚠️ Professional/collaborator using fallback route (should use profile check)');
      return '/axa/pre-registration'; // Fallback to pre-registration
    }

    // All other roles go to login page
    return '/login';
  }

  /**
   * Checks if user has all of the specified roles
   */
  hasAllRoles(roles: UserRole[]): boolean {
    return roles.every(role => this.hasRole(role));
  }

  /**
   * Gets all user roles
   */
  getUserRoles(): UserRole[] {
    return this.getCurrentUser()?.roles ?? [];
  }

  // ==================== Private Methods ====================

  /**
   * Builds Azure AD authorization URL for OAuth2 flow with PKCE
   * @param codeChallenge - PKCE code challenge
   */
  private buildAuthorizationUrl(codeChallenge: string): string {
    if (!environment.azureAd) {
      throw new Error('Azure AD configuration not found in environment');
    }

    const { tenantId, clientId, redirectUri, scopes } = environment.azureAd;
    
    // Generate simple CSRF state (no returnUrl embedded)
    const state = this.generateState();
    
    const params = new URLSearchParams({
      client_id: clientId,
      response_type: 'code',
      redirect_uri: redirectUri,
      response_mode: 'query',
      scope: scopes.join(' '),
      state: state,
      nonce: this.generateNonce(),
      code_challenge: codeChallenge,
      code_challenge_method: 'S256',
    });

    console.log('🔧 Authorization URL parameters:', {
      client_id: clientId,
      redirect_uri: redirectUri,
      code_challenge_method: 'S256',
      has_code_challenge: !!codeChallenge,
      state_length: state.length,
    });

    return `https://login.microsoftonline.com/${tenantId}/oauth2/v2.0/authorize?${params.toString()}`;
  }

  /**
   * Exchanges authorization code for access token via pal-dmn-core BFF.
   *
   * SECURITY: The code exchange happens server-side.  The backend returns only
   * the access_token in the response body and stores the refresh_token in an
   * HttpOnly Secure cookie — JavaScript never sees the refresh_token.
   */
  private async exchangeCodeForToken(code: string): Promise<string | null> {
    if (!environment.azureAd) return null;

    const codeVerifier = sessionStorage.getItem('pkce_code_verifier');
    if (!codeVerifier) {
      console.error('❌ PKCE code verifier not found in sessionStorage');
      return null;
    }

    const { redirectUri } = environment.azureAd;
    const url = `${environment.coreApiUrl}/api/auth/token-exchange`;

    console.log('🔄 Exchanging authorization code via BFF...');

    try {
      const data = await firstValueFrom(
        this.http.post<{ access_token: string; expires_in: number }>(url,
          { code, codeVerifier, redirectUri },
          { withCredentials: true } // allows the browser to store the HttpOnly cookie
        )
      );

      // Clear PKCE verifier — it is single-use
      sessionStorage.removeItem('pkce_code_verifier');

      console.log('✅ BFF token exchange successful');
      return data?.access_token ?? null;
    } catch (error) {
      console.error('❌ BFF token exchange failed:', error);
      return null;
    }
  }

  /**
   * Parses JWT token and extracts user profile
   * Now includes security validations
   */
  private parseToken(token: string): UserProfile | null {
    try {
      const payload = this.decodeJwtPayload(token);

      // Validate token claims
      if (!this.validateTokenClaims(payload)) {
        console.error('❌ Token validation failed');
        return null;
      }
      
      const extractedRoles = this.extractRoles(payload);

      // Merge server-provided effective roles (header/CIAM enrichment) when
      // enabled; falls back to JWT-decoded roles when unavailable.
      const effectiveRoles = this.mergeServerRoles(extractedRoles);

      // Extract professional_id from JWT if present (custom claim)
      const professionalIdFromToken = payload['professional_id'] || 
                                       payload['extension_professionalId'] || 
                                       null;
      
      // Extract email with multiple fallbacks for Azure AD tokens
      const email = payload.email || 
                    payload.preferred_username || 
                    payload['upn'] || 
                    payload['unique_name'] ||
                    (Array.isArray(payload['emails']) && payload['emails'].length > 0 ? payload['emails'][0] : null) ||
                    '';
      
      if (!email) {
        console.warn('⚠️ No email found in token payload. Available fields:', Object.keys(payload));
      }
      
      return {
        id: payload.sub || payload.oid || '',
        email: email,
        name: this.extractName(payload),
        roles: effectiveRoles,
        professionalId: professionalIdFromToken, // ✅ From JWT if available
        exp: payload.exp,
      };
    } catch (error) {
      console.error('❌ Error parsing token:', error);
      return null;
    }
  }

  /**
   * Validates JWT token claims for security
   * Checks: issuer, audience, required claims
   */
  private validateTokenClaims(payload: JwtPayload): boolean {
    // Skip validation if Azure AD not configured
    if (!environment.azureAd) {
      console.warn('⚠️ Azure AD not configured, skipping token validation');
      return true;
    }

    const { tenantId, clientId } = environment.azureAd;
    
    // 1. Validate Issuer (support both v1.0 and v2.0 tokens)
    const validIssuers = [
      `https://sts.windows.net/${tenantId}/`, // v1.0 tokens
      `https://login.microsoftonline.com/${tenantId}/v2.0`, // v2.0 tokens
    ];
    
    if (!validIssuers.includes(payload['iss'])) {
      console.error('❌ Invalid token issuer');
      console.error('   Expected one of:', validIssuers);
      console.error('   Received:', payload['iss']);
      return false;
    }

    // 2. Validate Audience (clientId)
    const audience = Array.isArray(payload['aud']) ? payload['aud'] : [payload['aud']];
    if (!audience.includes(clientId)) {
      console.error('❌ Invalid token audience');
      console.error('   Expected:', clientId);
      console.error('   Received:', payload['aud']);
      return false;
    }

    // 3. Validate required claims
    if (!payload.sub && !payload.oid) {
      console.error('❌ Token missing subject/oid claim');
      return false;
    }

    // 4. Validate token type (should be JWT)
    if (payload['typ'] && payload['typ'] !== 'JWT') {
      console.error('❌ Invalid token type:', payload['typ']);
      return false;
    }

    // 5. Check token version (prefer v2.0 tokens)
    if (payload['ver'] && payload['ver'] !== '2.0') {
      // v1.0 tokens are accepted; no log needed (benign known condition).
    }

    // 6. Validate tenant
    if (payload['tid'] && payload['tid'] !== tenantId) {
      console.error('❌ Token from wrong tenant');
      console.error('   Expected:', tenantId);
      console.error('   Received:', payload['tid']);
      return false;
    }

    return true;
  }

  /**
   * Decodes JWT token payload (without verification)
   */
  private decodeJwtPayload(token: string): JwtPayload {
    // Clean token before processing
    const cleanToken = token.replace(/\s+/g, '').trim();
    
    const parts = cleanToken.split('.');
    if (parts.length !== 3) {
      console.error('❌ Token has', parts.length, 'parts, expected 3');
      console.error('   Token starts with:', cleanToken.substring(0, 50));
      throw new Error('Invalid JWT token format');
    }

    try {
      const payload = parts[1];
      const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
      return JSON.parse(decoded);
    } catch (error) {
      console.error('❌ Error decoding token payload:', error);
      console.error('   Payload part starts with:', parts[1].substring(0, 50));
      throw new Error('Invalid JWT token encoding');
    }
  }

  /**
   * Extracts user name from token payload
   */
  private extractName(payload: JwtPayload): string {
    if (payload.name) return payload.name;
    if (payload.given_name && payload.family_name) {
      return `${payload.given_name} ${payload.family_name}`;
    }
    return payload.preferred_username || payload.email || 'Unknown User';
  }

  /** Logical roles supported by the application. */
  private static readonly VALID_ROLES: UserRole[] = [
    'admin',
    'backoffice_admin',
    'backoffice_professional',
    'backoffice_supplier',
    'professional',
    'collaborator',
  ];

  /**
   * Merges the server-provided effective roles (header/CIAM enrichment) with
   * the roles decoded from the JWT. When server roles are unavailable, the JWT
   * roles are returned unchanged (graceful fallback).
   */
  private mergeServerRoles(jwtRoles: UserRole[]): UserRole[] {
    if (!environment.useServerRoles || !this._serverRoles || this._serverRoles.length === 0) {
      return jwtRoles;
    }
    return [...new Set<UserRole>([...jwtRoles, ...this._serverRoles])];
  }

  /**
   * Fetches the effective roles from pal-dmn-core (GET /api/v1/me/roles) which
   * reflect the x-user-roles header (CIAM enrichment) or, for onboarding users,
   * a direct CIAM lookup. Results are cached in {@link _serverRoles} so
   * getCurrentUser()/parseToken() reflect them. Fails silently, falling back to
   * client-side JWT decoding.
   */
  async loadServerRoles(): Promise<void> {
    if (!environment.useServerRoles) {
      return;
    }
    const url = `${environment.coreApiUrl}/api/v1/me/roles`;
    try {
      const resp = await firstValueFrom(
        this.http.get<{ status: boolean; message: string; data: string[] }>(url)
      );
      const raw = resp?.data ?? [];
      const mapped = raw
        .map(r => (r || '').trim().toLowerCase())
        .filter((r): r is UserRole =>
          (AuthService.VALID_ROLES as string[]).includes(r));
      this._serverRoles = [...new Set<UserRole>(mapped)];
      console.log('✅ Server roles loaded:', this._serverRoles);
      // Refresh subscribers with the enriched profile.
      this.getCurrentUser();
    } catch (e) {
      console.warn('⚠️ Could not load server roles, falling back to JWT roles:', e);
    }
  }

  /**
   * Extracts user roles from token payload
   */
  private extractRoles(payload: JwtPayload): UserRole[] {
    const validRoles: UserRole[] = [
      'admin',
      'backoffice_admin',
      'backoffice_professional',
      'backoffice_supplier',
      'professional',
      'collaborator',
    ];

    // Reverse map: PAL role code (env-specific) -> logical role name.
    // Codes differ between prod and non-prod; the logical role does not.
    // Dual-support: if an incoming role is not a known code, it is kept as-is
    // so legacy logical role names in the JWT continue to work during rollout.
    const codeToRole = new Map<string, string>();
    const roleCodes = environment.roleCodes;
    if (roleCodes) {
      for (const [logicalRole, code] of Object.entries(roleCodes)) {
        if (code) {
          codeToRole.set(String(code).toLowerCase(), logicalRole.toLowerCase());
        }
      }
    }

    let roles: string[] = [];
    
    // 1. Check 'roles' claim (Azure AD App Roles)
    if (payload.roles && Array.isArray(payload.roles)) {
      roles = [...roles, ...payload.roles];
    }
    
    // 2. Check 'groups' claim (Azure AD Groups)
    // Groups might contain role names directly or GUIDs that need mapping
    if (payload['groups'] && Array.isArray(payload['groups'])) {
      const groups = payload['groups'] as string[];
      
      // Try to extract roles from group names
      // Groups might be role names directly or contain role info
      groups.forEach(group => {
        // Check if group name matches a valid role
        const lowerGroup = group.toLowerCase();
        validRoles.forEach(validRole => {
          if (lowerGroup.includes(validRole.toLowerCase())) {
            roles.push(validRole);
          }
        });
      });
    }
    
    // 3. Check extension attributes (custom claims)
    if (payload['extension_UserRole']) {
      const extensionRole = payload['extension_UserRole'];
      if (typeof extensionRole === 'string') {
        roles.push(extensionRole);
      } else if (Array.isArray(extensionRole)) {
        roles = [...roles, ...extensionRole];
      }
    }
    
    // 4. Check wids claim (Azure AD role template IDs)
    if (payload['wids'] && Array.isArray(payload['wids'])) {
      // wids are GUIDs, would need mapping table if used
    }
    
    // Filter to valid roles only and remove duplicates
    const uniqueRoles = [...new Set(roles)];
    
    const filteredRoles: UserRole[] = [];
    
    for (const role of uniqueRoles) {
      const trimmedRole = (role || '').trim();
      // Translate PAL role code -> logical role when applicable (dual-support:
      // unknown values fall through unchanged, preserving legacy role names).
      const normalizedRole = codeToRole.get(trimmedRole.toLowerCase()) ?? trimmedRole;
      const lowerRole = normalizedRole.toLowerCase();
      
      // Check if this role matches any valid role
      for (const validRole of validRoles) {
        if (lowerRole === validRole.toLowerCase()) {
          // Always push the canonical lowercase validRole, NOT trimmedRole.
          // This ensures hasRole() (which uses strict .includes()) works regardless
          // of the casing used in the JWT (e.g. BACKOFFICE_ADMIN vs backoffice_admin).
          filteredRoles.push(validRole);
          break;
        }
      }
    }
    
    if (filteredRoles.length === 0) {
      console.warn('⚠️ No valid roles found in token. Available claims:', Object.keys(payload));
    }
    
    return filteredRoles;
  }

  /**
   * Checks if token is expired or invalid
   * Validates: exp, nbf, iat claims with time skew tolerance
   */
  private isTokenExpired(token: string): boolean {
    try {
      const payload = this.decodeJwtPayload(token);
      const now = Math.floor(Date.now() / 1000);
      const timeSkewSeconds = 300; // 5 minutes tolerance for clock skew
      
      // 1. Check expiration (exp)
      if (!payload.exp) {
        console.warn('⚠️ Token missing exp claim');
        return false; // Allow tokens without exp (though unusual)
      }
      
      if (payload.exp < (now - timeSkewSeconds)) {
        console.log('⏰ Token expired:', new Date(payload.exp * 1000).toISOString());
        return true;
      }
      
      // 2. Check not-before (nbf)
      if (payload['nbf'] && payload['nbf'] > (now + timeSkewSeconds)) {
        console.error('❌ Token not yet valid (nbf):', new Date(payload['nbf'] * 1000).toISOString());
        return true;
      }
      
      // 3. Check issued-at (iat) - reject tokens from future
      if (payload['iat'] && payload['iat'] > (now + timeSkewSeconds)) {
        console.error('❌ Token issued in future (iat):', new Date(payload['iat'] * 1000).toISOString());
        return true;
      }
      
      // 4. Check if token is too old (max 24 hours from issuance)
      if (payload['iat']) {
        const tokenAge = now - payload['iat'];
        const maxAge = 24 * 60 * 60; // 24 hours
        
        if (tokenAge > maxAge) {
          console.warn('⚠️ Token older than 24 hours, consider refreshing');
        }
      }
      
      return false;
    } catch (error) {
      console.error('❌ Error checking token expiration:', error);
      return true; // Treat parsing errors as expired
    }
  }

  /**
   * Verifies that the current user has at least one valid role.
   * Used by AuthGuard for route protection.
   * 
   * NOTE: Does NOT attempt role assignment - that happens in handleCallback()
   * via runPostLoginFlow(). This is a pure check.
   * 
   * @returns Promise<boolean> - true if user has at least one valid role, false otherwise
   */
  async verifyUserAccess(): Promise<boolean> {
    const user = this.getCurrentUser();

    if (!user) {
      console.warn('⚠️ [VerifyAccess] No user profile found');
      this.accessDenialReason$.next('Profilo utente non trovato');
      return false;
    }

    const validRoles = user.roles?.filter(role => role && role.trim().length > 0) ?? [];

    if (validRoles.length === 0) {
      console.warn('⚠️ [VerifyAccess] User has no valid roles');
      this.accessDenialReason$.next('Nessun ruolo assegnato all\'utente. Contattare l\'amministratore.');
      return false;
    }

    console.log('✅ [VerifyAccess] User has valid roles:', validRoles);
    this.accessDenialReason$.next(null);
    return true;
  }

  /**
   * Loads user profile from sessionStorage on service init.
   * The access_token is intentionally NOT loaded from any storage.
   * It lives in memory only and is restored via silent-refresh on startup.
   */
  private loadUserFromStorage(): void {
    // Migrate legacy localStorage entry to sessionStorage on first run
    const legacyProfile = localStorage.getItem('user_profile');
    if (legacyProfile && !sessionStorage.getItem('user_profile')) {
      sessionStorage.setItem('user_profile', legacyProfile);
      localStorage.removeItem('user_profile');
      localStorage.removeItem('user');
      localStorage.removeItem('access_token'); // clean up the old insecure entry
    }

    const profileStr = sessionStorage.getItem('user_profile');
    if (profileStr) {
      try {
        const profile = JSON.parse(profileStr);
        this.currentUserSubject.next(profile);
        console.log('✅ User profile loaded from sessionStorage (token will be restored via silent-refresh)');
      } catch (error) {
        console.error('❌ Error loading user profile from storage:', error);
      }
    }
  }

  /**
   * Generates random state for OAuth2 flow with optional embedded returnUrl
   * Format: {csrf_token}|{base64_encoded_returnUrl}
   * If no returnUrl, just returns the CSRF token
   */
  private generateStateWithReturnUrl(returnUrl?: string): string {
    const csrfToken = this.generateRandomString(32);
    
    if (!returnUrl) {
      return csrfToken;
    }
    
    // Encode returnUrl in base64 to safely pass through OAuth flow
    const encodedReturnUrl = btoa(returnUrl);
    return `${csrfToken}|${encodedReturnUrl}`;
  }

  /**
   * Extracts returnUrl from OAuth state parameter
   * @param state - OAuth state parameter from callback
   * @returns Decoded returnUrl or null if not present
   */
  private extractReturnUrlFromState(state: string): string | null {
    try {
      // Check if state contains returnUrl (format: csrf|base64url)
      const parts = state.split('|');
      if (parts.length === 2) {
        // Decode base64 returnUrl
        const decodedReturnUrl = atob(parts[1]);
        console.log('🔓 Decoded returnUrl from state:', decodedReturnUrl);
        return decodedReturnUrl;
      }
      return null;
    } catch (error) {
      console.warn('⚠️ Error extracting returnUrl from state:', error);
      return null;
    }
  }

  /**
   * Generates random state for OAuth2 flow (CSRF protection)
   * @deprecated Use generateStateWithReturnUrl instead
   */
  private generateState(): string {
    return this.generateRandomString(32);
  }

  /**
   * Generates random nonce for OpenID Connect
   */
  private generateNonce(): string {
    return this.generateRandomString(32);
  }

  /**
   * Generates random string for security tokens
   */
  private generateRandomString(length: number): string {
    const array = new Uint8Array(length);
    crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
  }

  /**
   * Generates PKCE code verifier (random string)
   */
  private generateCodeVerifier(): string {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return this.base64UrlEncode(array);
  }

  /**
   * Generates PKCE code challenge from verifier using SHA-256
   */
  private async generateCodeChallenge(verifier: string): Promise<string> {
    // Check if crypto.subtle is available (requires HTTPS or localhost)
    if (typeof window === 'undefined' || 
        typeof window.crypto === 'undefined' || 
        typeof window.crypto.subtle === 'undefined' ||
        !window.crypto.subtle) {
      const errorMsg = 
        '❌ crypto.subtle is not available!\n' +
        'PKCE requires HTTPS or localhost.\n' +
        'Current URL: ' + (typeof window !== 'undefined' ? window.location.href : 'unknown') + '\n' +
        'The application MUST be served over HTTPS (or localhost for development).';
      
      console.error(errorMsg);
      console.error('crypto:', typeof window !== 'undefined' ? window.crypto : 'undefined');
      console.error('crypto.subtle:', typeof window !== 'undefined' && window.crypto ? window.crypto.subtle : 'undefined');
      
      throw new Error(
        'Secure context required: crypto.subtle is not available. ' +
        'Please ensure the application is served over HTTPS or localhost.'
      );
    }

    const encoder = new TextEncoder();
    const data = encoder.encode(verifier);
    const hash = await window.crypto.subtle.digest('SHA-256', data);
    return this.base64UrlEncode(new Uint8Array(hash));
  }

  /**
   * Base64 URL encoding (without padding)
   */
  private base64UrlEncode(array: Uint8Array): string {
    const base64 = btoa(String.fromCharCode(...array));
    return base64
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '');
  }
}
